import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import queryString from 'query-string';
import {
  Route,
  Redirect,
  useHistory,
} from 'react-router-dom';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { authenticatedSelector } from 'AppSrc/core/auth/auth-selectors';
import { setAuthorizationFromJWTToken } from 'AppSrc/core/auth/auth-actions';

/* eslint-disable react/jsx-props-no-spreading */
const PrivateRouteDisplay = React.memo(({
  location,
  component: Component,
  authenticated,
  setAuthorization,
  ...rest
}) => {

  // useHistory hook
  // https://reacttraining.com/react-router/web/api/Hooks/usehistory
  const history = useHistory();

  // extract JWT token from query string
  useEffect(
    () => {

      if (!authenticated && location.search) {

        const queryStringParams = queryString.parse(location.search);

        // Process the JWT token from query string, use it to auth and remove it from visible url
        if (queryStringParams.jwt) {

          // console.log('PrivateRoute QUERY STRING with JWT: ', queryStringParams);

          // set authorization in redux with JWT token
          const JWTToken = queryStringParams.jwt;
          setAuthorization(JWTToken);

          // Remove JWT token from query string params while keeping other params
          // so user can't just hack the token from the url bar
          const queryStringParamsHideToken = {
            ...queryStringParams,
            jwt: null,
            // MM: I'm leaving this commented out as a proof that I tested it with other query params, in case we need them later, on a search page for example
            // Jonathan did the same server side, make sure other params don't get erased, eve though we dont need them yet
            // test: 'query-remove-jwt-but-keep-other-params',
          };
          // skipNull =  handy way to get rif of query param whose value is null
          // https://www.npmjs.com/package/query-string
          const queryStringHideToken = queryString.stringify(queryStringParamsHideToken, {
            skipNull: true,
          });

          // create a version of the current location that is identical except from removed JWT query string param
          const locationHideToken = {
            ...location,
            search: queryStringHideToken,
          };

          // replace in history current location by identical one without JWT param
          history.replace(locationHideToken);
        }
      }
    },
    [authenticated, location, history, setAuthorization],
  );


  return (
    <Route
      {...rest}
      render={(props) => (
        authenticated === true
          ? (
            <Component
              {...props}
            />
          ) : (
            <Redirect
              to={{
                pathname: '/login',
                state: { from: props.location },
              }}
            />
          )
      )}
    />
  );
});

PrivateRouteDisplay.propTypes = {
  location: ReactRouterPropTypes.location,
  authenticated: PropTypes.bool,
  component: PropTypes.elementType,
  setAuthorization: PropTypes.func,
};

/* --- PrivateRoute Redux-connected container component --- */

const mapStateToProps = (state, ownProps) => ({
  authenticated: authenticatedSelector(state),
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  setAuthorization: (JWTToken) => {
    dispatch(setAuthorizationFromJWTToken(JWTToken));
  },
});

const PrivateRoute = connect(
  mapStateToProps,
  mapDispatchToProps,
)(PrivateRouteDisplay);

export {
  PrivateRouteDisplay,
  PrivateRoute,
};
